/**
 * @voltxampere/web — `<vxa-topbar>` (Sprint 11, Sprint 18 Paket B B1).
 *
 * Three slots:
 *   left   → wordmark + circuit title placeholder
 *   center → 5 nav-tabs (Açılış / Simülatör / Devreler / Inspector / Ayarlar)
 *   right  → import / export / preset chips + locale toggle + user menu
 *
 * Wordmark is rendered as inline SVG-free text: Cormorant
 * Infant for "Volt", italic Cormorant + brass for "×",
 * Cormorant for "Ampere".
 *
 * Sprint 18 Paket B B1 — nav-tabs:
 *   - 5 tab, mockup zenit-platform.html birebir layout (padding
 *     22px 20px, JetBrains Mono 10.5px, 0.22em letter-spacing)
 *   - Roman numeral badge (I-V) tab adının solunda, italic, brass-dim
 *   - Active state: brass color + 1.5px brass border-bottom
 *   - Click → setPane(); 1-5 keyboard shortcut'ları catalog.ts'de
 *   - currentPane signal'i SignalController ile dinleniyor
 *
 * Copyright © 2026 360 Studio
 * Proprietary — All rights reserved.
 */

import { LitElement, html, css } from "lit";
import { customElement } from "lit/decorators.js";

import { currentCircuit } from "../store/circuit.js";
import { toggleLocale } from "../store/locale.js";
import { openImportDialog, openExportDialog } from "../store/import-export.js";
import { currentPane, PANE_IDS, PANE_NUMERALS, setPane, type PaneId } from "../store/router.js";
import { t, type TranslationKey } from "../i18n/index.js";
import { SignalController } from "../lit-utils/signal-controller.js";
import { VERSION, devModeVersionLabel, publicStatusLabel } from "../version.js";
// Sprint 23 I1 — auto-save indicator + manual Cmd+S save chip.
// Sprint 24 A0 (bug_012 fix) — autoSaveError import for symmetric
// error handling in runManualSave.
import {
  autoSaveError,
  autoSaveLastSavedAt,
  autoSaveStatus,
  type AutoSaveStatus,
} from "../store/auto-save.js";
import { putCircuit } from "@voltxampere/persistence";
// Sprint 53 — toolbar speed dropdown registration (side-effect import).
import "./vxa-speed-control.js";
// Sprint 56c — ERC badge registration (side-effect import).
import "./vxa-erc-badge.js";
// Sprint 80.Y L-A-2 — osiloskop reversibility (Şefim #2 closure).
import { waveformPaneVisible, toggleWaveformPane } from "../store/waveform-visibility.js";

const PANE_LABEL_KEYS: Readonly<Record<PaneId, TranslationKey>> = {
  landing: "nav.tab.landing",
  simulator: "nav.tab.simulator",
  presets: "nav.tab.presets",
  inspector: "nav.tab.inspector",
  settings: "nav.tab.settings",
};

@customElement("vxa-topbar")
export class VxaTopbar extends LitElement {
  private readonly _circuit = new SignalController(this, currentCircuit);
  private readonly _pane = new SignalController(this, currentPane);
  // Sprint 23 I1 — re-render when auto-save status changes.
  private readonly _saveStatus = new SignalController(this, autoSaveStatus);
  private readonly _saveLast = new SignalController(this, autoSaveLastSavedAt);
  // Sprint 80.Y L-A-2 — reactive waveform pane visibility for the
  // toggle chip; aria-pressed flips per signal.
  private readonly _waveVisible = new SignalController(this, waveformPaneVisible);

  override connectedCallback(): void {
    super.connectedCallback();
    window.addEventListener("vxa-manual-save", this._onManualSaveEvent);
  }

  override disconnectedCallback(): void {
    super.disconnectedCallback();
    window.removeEventListener("vxa-manual-save", this._onManualSaveEvent);
  }

  // Sprint 23 I1 — Cmd+S → ShortcutManager dispatches custom event;
  // topbar handles via runManualSave (single save flow).
  private readonly _onManualSaveEvent = (): void => {
    void this.runManualSave();
  };

  static override styles = css`
    :host {
      display: grid;
      grid-template-columns: auto 1fr auto;
      align-items: center;
      height: var(--topbar-height);
      padding: 0 var(--s-6);
      border-bottom: 1px solid var(--ivory-wisp);
      background: var(--ink);
      flex-shrink: 0;
    }

    .left,
    .right,
    .center {
      display: flex;
      align-items: center;
      gap: var(--s-3);
    }

    .left {
      gap: var(--s-5);
    }

    .center {
      justify-content: center;
      gap: 0;
      align-self: stretch;
    }

    .right {
      justify-content: flex-end;
      gap: var(--s-3);
      /* Sprint 25 R5 R5-VIS-4 — overflow protection. min-width: 0
         lets flex children shrink past their content size when the
         topbar is tight (TR locale + auto-save status + 4 chips +
         menu = roughly 1.3x the EN baseline). Without this the
         host grid template (auto 1fr auto) forces .center to shrink
         to min-content, clipping longer nav tab labels (SIMULATOR,
         DEVRELER, INSPECTOR, AYARLAR). With it, the auto-save
         indicator below shrinks first via its own max-width. */
      min-width: 0;
    }

    /* Sprint 23 I1 auto-save status pill. Sprint 28 K4 (F-V-05
       structural relayout): the indicator is rendered AFTER the
       locale toggle (utility region), so the long-string ellipsis
       cap shipped in Sprint 25 R5 R5-VIS-4 is no longer needed —
       the chip group sits between the indicator and the central
       nav, absorbing growth before it can clip nav-tab labels.
       The .center min-width:0 invariant (above) is kept as the
       independent nav-clip safety net. Full status text is now
       always visible to the user (was 140px ellipsis cap before). */
    .auto-save-indicator {
      font-family: var(--font-mono);
      font-size: var(--fs-mono-tiny);
      letter-spacing: 0.18em;
      color: var(--ivory-dim);
    }

    .wordmark {
      font-family: var(--font-display);
      font-weight: 300;
      font-size: 24px;
      letter-spacing: -0.02em;
      color: var(--ivory);
      line-height: 1;
      user-select: none;
    }

    .wordmark em {
      font-style: italic;
      color: var(--brass);
      font-weight: 300;
      margin: 0 0.18em;
    }

    .divider {
      width: 1px;
      height: 24px;
      background: var(--ivory-wisp);
    }

    .title {
      font-family: var(--font-body);
      font-style: italic;
      font-size: var(--fs-small);
      color: var(--ivory-dim);
      letter-spacing: 0.01em;
      max-width: 240px;
      white-space: nowrap;
      overflow: hidden;
      text-overflow: ellipsis;
    }

    /* Sprint 17 — dev-only build label. */
    .build-label {
      font-family: var(--font-mono);
      font-size: 10px;
      letter-spacing: 0.18em;
      text-transform: uppercase;
      color: var(--brass-faint);
      opacity: 0.65;
      user-select: none;
    }

    /* Sprint 18 B1 — nav-tabs (mockup zenit-platform 2 birebir).
       padding 22px 20px taban yüksekliği 64px topbar'a sığdırır;
       border-bottom transparent → brass active'de görünür. */
    .nav-tab {
      padding: 22px 20px;
      font-family: var(--font-mono);
      font-size: 10.5px;
      letter-spacing: 0.22em;
      text-transform: uppercase;
      color: var(--ivory-dim);
      cursor: pointer;
      border: none;
      background: transparent;
      border-bottom: 1.5px solid transparent;
      transition:
        color 180ms ease,
        border-color 180ms ease;
      position: relative;
      align-self: stretch;
      display: inline-flex;
      align-items: center;
      gap: var(--s-2);
    }

    .nav-tab:hover {
      color: var(--ivory);
    }

    .nav-tab[aria-current="page"] {
      color: var(--brass);
      border-bottom-color: var(--brass);
    }

    .nav-tab:focus-visible {
      outline: 1px solid var(--brass);
      outline-offset: -3px;
    }

    .nav-tab .num {
      font-family: var(--font-display);
      font-style: italic;
      color: var(--brass-dim);
      font-weight: 300;
      font-size: 13px;
      letter-spacing: 0;
      transition: color 180ms ease;
    }

    .nav-tab[aria-current="page"] .num {
      color: var(--brass);
    }

    .chip {
      font-family: var(--font-mono);
      font-size: var(--fs-mono-tiny);
      letter-spacing: 0.22em;
      color: var(--brass);
      border: 1px solid var(--brass-faint);
      padding: 4px 10px;
      cursor: pointer;
      background: transparent;
      /* Sprint 80.X Sandık Kapağı (F-V-201) — prevent label wrap
         when chip padding is tightened at narrow breakpoints. The
         media-query block below shrinks padding but the chip text
         (TR "İçe Aktar", "Dışa Aktar") must stay on one line. */
      white-space: nowrap;
      transition:
        border-color var(--transition-hover),
        background var(--transition-hover),
        color var(--transition-hover);
    }

    .chip:hover {
      background: var(--brass-ghost);
      color: var(--brass-hot);
      border-color: var(--brass);
    }

    /* Sprint 26 Lane 4 R5-IA-6 — keyboard shortcut hint chip.
       Inline kbd elements inside an action button surface the
       canonical accelerator to first-time users without forcing
       them to hover for the title tooltip. The chip is decorative
       (aria-hidden) so screen readers do not announce it twice;
       title= already carries the accessible accelerator string. */
    .chip kbd {
      display: inline-block;
      margin-inline-start: 8px;
      padding: 1px 4px;
      font-family: var(--font-mono);
      font-size: 10px;
      letter-spacing: 0.06em;
      color: var(--ivory-dim);
      background: var(--ink-soft, transparent);
      border: 1px solid var(--brass-faint);
      border-radius: 2px;
      vertical-align: baseline;
    }

    .menu {
      font-family: var(--font-mono);
      font-size: var(--fs-mono-tiny);
      letter-spacing: 0.22em;
      color: var(--ivory-dim);
      text-transform: uppercase;
      user-select: none;
      cursor: not-allowed;
    }

    /* Sprint 80.X Sandık Kapağı (F-V-201, ADR-0118) — responsive
       chip collapse for narrow viewports. The Reckoning audit
       caught the dev-mode build label + .right cluster overlapping
       INSPECTOR + AYARLAR tab labels at 1440×900 (canonical MacBook
       13" / probe viewport).

       Diagnosis: TR locale chip labels ("Kaydet" + "İçe Aktar" +
       "Dışa Aktar") with ⌘S/⌘I/⌘E kbd suffixes plus speed-control
       + erc-badge + locale toggle + auto-save + user-menu push the
       .right cluster ~600px wide; .left + 5 nav-tabs need another
       ~1000px; total ~1600px minimum to render without overlap.

       Fix without UI restructure: progressively trim decorations:
         - le 1600px: hide the inline kbd accelerator suffix
           (-~120px). The chip's title= attribute still surfaces
           the shortcut on hover, and the canonical shortcut
           overlay (Cmd+?) lists everything.
         - ≤ 1440px: tighten chip padding 4px 10px → 4px 6px and
           drop the auto-save indicator letter-spacing (-~40px).
         - ≤ 1280px: hide the .build-label entirely (production
           already hides it via VERSION.mode check; this catches
           dev users on small displays).

       A future enhancement (Sprint 81+) may move the chips into
       a 3-dot overflow menu — out of scope for this hotfix.
       Dev-mode build label is preserved per Sprint 80.X D-80X-3
       — it auto-hides in production via VERSION.mode === "development". */
    @media (max-width: 1600px) {
      .chip kbd {
        display: none;
      }
    }

    @media (max-width: 1440px) {
      .chip {
        padding: 4px 6px;
      }
      .auto-save-indicator {
        /* The auto-save status is a low-stakes informational pill
           ("Kaydedildi · 12s önce"); on narrow viewports we drop it
           entirely so the chips can shrink to non-overlapping. The
           save state remains visible via the Save chip itself
           (which switches to error/saving variants). */
        display: none;
      }
      .menu {
        /* User menu ("HESAP") is decorative (cursor: not-allowed)
           until Sprint 81+ adds real account features. Drop on
           narrow viewports to free the right-column footprint. */
        display: none;
      }
      .build-label {
        /* Sprint 80.X D-80X-3: do NOT hide the dev/prod build label
           at 1440 (developers + qa surface need it). Cap its width
           with an ellipsis so it sits in .left without elbowing
           the .center nav tabs. The full label remains in the DOM
           and is readable via the title= attribute or via the
           AYARLAR → Hakkında pane. */
        max-width: 90px;
        white-space: nowrap;
        overflow: hidden;
        text-overflow: ellipsis;
      }
    }

    @media (max-width: 1280px) {
      .build-label {
        display: none;
      }
    }
  `;

  override render() {
    const circuitName = this._circuit.value?.name ?? t("topbar.untitled");
    const isDev = VERSION.mode === "development";
    const buildLabel = isDev ? devModeVersionLabel() : publicStatusLabel();
    const activePane = this._pane.value;

    return html`
      <div class="left">
        <span class="wordmark">Volt <em>×</em> Ampere</span>
        <span class="divider"></span>
        <span class="title">${circuitName}</span>
        <span
          class="build-label"
          data-testid="build-label"
          data-status=${VERSION.status}
          title=${buildLabel}
          >${buildLabel}</span
        >
      </div>
      <nav class="center" aria-label="Pages">
        ${PANE_IDS.map((pane) => this.renderTab(pane, activePane === pane))}
      </nav>
      <div class="right" @click=${this.handleAction}>
        <button class="chip" data-action="save" type="button" title="Cmd+S">
          ${t("topbar.save")}<kbd aria-hidden="true">⌘S</kbd>
        </button>
        <button class="chip" data-action="import" type="button" title="Cmd+I">
          ${t("topbar.import")}<kbd aria-hidden="true">⌘I</kbd>
        </button>
        <button class="chip" data-action="export" type="button" title="Cmd+E">
          ${t("topbar.export")}<kbd aria-hidden="true">⌘E</kbd>
        </button>
        <vxa-speed-control></vxa-speed-control>
        <vxa-erc-badge></vxa-erc-badge>
        <button
          class="chip"
          data-action="toggle-waveform"
          type="button"
          aria-pressed=${waveformPaneVisible.value ? "true" : "false"}
          title=${t("topbar.waveform.toggle.title")}
          aria-label=${waveformPaneVisible.value
            ? t("topbar.waveform.toggle.hide")
            : t("topbar.waveform.toggle.show")}
        >
          ${waveformPaneVisible.value ? "▾" : "▴"} ${t("topbar.waveform.toggle.label")}<kbd
            aria-hidden="true"
            >⌘⇧O</kbd
          >
        </button>
        <button class="chip" type="button" @click=${toggleLocale}>
          ${t("topbar.locale.toggle")}
        </button>
        <span class="auto-save-indicator" data-testid="auto-save-indicator">
          ${this.renderAutoSaveStatus()}
        </span>
        <span class="menu">${t("topbar.user.menu")}</span>
      </div>
    `;
  }

  private renderTab(pane: PaneId, active: boolean) {
    return html`<button
      class="nav-tab"
      type="button"
      data-pane=${pane}
      data-testid=${`nav-tab-${pane}`}
      aria-current=${active ? "page" : "false"}
      @click=${() => {
        setPane(pane);
      }}
    >
      <span class="num">${PANE_NUMERALS[pane]}</span>${t(PANE_LABEL_KEYS[pane])}
    </button>`;
  }

  private handleAction = (e: Event): void => {
    const path = e.composedPath();
    const target = path.find(
      (n): n is HTMLElement => n instanceof HTMLElement && typeof n.dataset.action === "string",
    );
    if (!target) return;
    switch (target.dataset.action) {
      case "save":
        // Sprint 23 I1 — manual save: flush currentCircuit to IDB
        // immediately (bypass auto-save debounce). Async; status
        // signal updates surface in the UI naturally.
        void this.runManualSave();
        break;
      case "import":
        openImportDialog();
        break;
      case "export":
        openExportDialog();
        break;
      case "toggle-waveform":
        // Sprint 80.Y L-A-2 — closes Şefim #2 (osiloskop gizledim,
        // açacak yer yok). The topbar chip is the persistent reveal
        // affordance — always visible regardless of the waveform
        // pane's own in-pane state.
        toggleWaveformPane();
        break;
      default:
        break;
    }
  };

  /**
   * Sprint 23 I1 — manual save (Cmd+S / topbar Save chip). Calls
   * putCircuit directly so the user sees an immediate confirmation
   * rather than waiting on the 5s auto-save debounce.
   *
   * Sprint 24 A0 (bug_012 fix) — symmetric error handling. The
   * Sprint 23 implementation cleared `autoSaveError` only on
   * success and left the error chip pinned in the topbar
   * indefinitely on failure. /ultrareview flagged the asymmetry:
   * - autoSaveError now clears at the start of every attempt
   *   (mirrors auto-save.ts:120)
   * - catch branch sets autoSaveError.value to the message
   *   (mirrors auto-save.ts:145)
   * - error state auto-resets to idle after 4s (longer than
   *   success so the user has time to read the chip)
   */
  private async runManualSave(): Promise<void> {
    const circuit = currentCircuit.value;
    if (!circuit) return;
    autoSaveStatus.value = "saving";
    autoSaveError.value = null;
    try {
      await putCircuit(circuit);
      autoSaveStatus.value = "saved";
      autoSaveLastSavedAt.value = Date.now();
      // Drop back to idle after 2s (matches auto-save flow).
      setTimeout(() => {
        if (autoSaveStatus.value === "saved") {
          autoSaveStatus.value = "idle";
        }
      }, 2_000);
    } catch (err) {
      autoSaveStatus.value = "error";
      autoSaveError.value = err instanceof Error ? err.message : String(err);
      // Auto-reset error chip after 4s — gives the user time to
      // read the message but doesn't pin it forever.
      setTimeout(() => {
        if (autoSaveStatus.value === "error") {
          autoSaveStatus.value = "idle";
        }
      }, 4_000);
      if (import.meta.env.DEV) {
        console.warn("[VXA save] manual save failed:", err);
      }
    }
  }

  /**
   * Sprint 23 I1 — auto-save status indicator label. Empty for
   * idle (no clutter); shows "Kaydediliyor..." / "Kaydedildi"
   * during transitions. Last-saved timestamp formatted HH:mm
   * when available.
   */
  private renderAutoSaveStatus(): string {
    const status: AutoSaveStatus = this._saveStatus.value;
    if (status === "idle") {
      const last = this._saveLast.value;
      if (last !== null) {
        const date = new Date(last);
        const hh = String(date.getHours()).padStart(2, "0");
        const mm = String(date.getMinutes()).padStart(2, "0");
        return t("topbar.autoSave.savedAt", { time: `${hh}:${mm}` });
      }
      return "";
    }
    if (status === "pending") return t("topbar.autoSave.pending");
    if (status === "saving") return t("topbar.autoSave.saving");
    if (status === "saved") return t("topbar.autoSave.saved");
    return t("topbar.autoSave.error"); // status === "error" — exhaustive
  }
}

declare global {
  interface HTMLElementTagNameMap {
    "vxa-topbar": VxaTopbar;
  }
}
